import { Image, Text, View } from "@react-pdf/renderer";
import { isEmpty } from "lodash";
import { PDFSerialNumber } from "../serial-number";
import { PDFFooterLogoS3, PDFStyleConfigs } from "../../configs";
import { AnalysisDTO } from "@models/platform-analysis/entities/analysis";
import { AnalysisSummaryDTO } from "@models/platform-analysis/entities/steps/summary";
import { format } from "date-fns";
import { DateFormat } from "@constants/format";
import { useAnalysisPDFStyles } from "@hooks/useAnalysisPDFStyle";

interface PDFPageFooterProps {
  analysis?: AnalysisDTO;
  pageIndex?: number;
  totalPage?: number;
  isFirstPage?: boolean;
  isReview?: boolean;
}

export const PDFPageFooter = (props: PDFPageFooterProps) => {
  return props?.isFirstPage ? (
    <FirstPageFooter />
  ) : props.isReview ? (
    <ReviewPageFooter {...props} />
  ) : (
    <PageFooter {...props} />
  );
};

const FirstPageFooter = () => {
  const { pageStyle, typographyStyle } = useAnalysisPDFStyles();

  return (
    <View
      style={{
        ...pageStyle.footer,
        height: PDFStyleConfigs.FirstPageFooterHeight,
        marginRight: -45,
        marginBottom: 0,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "stretch",
      }}
    >
      <View
        style={{
          flexDirection: "column",
          justifyContent: "flex-end",
          alignItems: "flex-end",
          width: 150,
        }}
      >
        <Text
          style={{
            ...typographyStyle.subtitle,
            color: "#FFFFFF",
          }}
        >
          www.suitabilityhub.com
        </Text>
      </View>

      <Image
        src={PDFFooterLogoS3}
        style={{
          width: 330,
          height: 250,

          right: 0,
          bottom: -30,
          position: "absolute",
        }}
      />
    </View>
  );
};

const PageFooter = ({
  analysis,
  pageIndex = 0,
  totalPage,
}: PDFPageFooterProps) => {
  const { pageStyle, typographyStyle } = useAnalysisPDFStyles();

  const { ownerName, supplierName, adviserName, practiceName, serial } =
    analysis?.summary ?? new AnalysisSummaryDTO();

  return (
    <View
      style={{
        ...pageStyle.footer,
        height:
          (practiceName?.length ?? 0) + (adviserName?.length ?? 0) > 90
            ? PDFStyleConfigs.PageFooterDoubleLinesHeight
            : pageStyle.footer.height,
      }}
    >
      {!isEmpty(adviserName) && !isEmpty(practiceName) ? (
        <Text style={{ ...typographyStyle.body2, textAlign: "center" }}>
          {`This analysis is for: `}
          <Text style={{ ...typographyStyle.body2, fontWeight: "bold" }}>
            {adviserName}
          </Text>
          {` from `}
          <Text style={{ ...typographyStyle.body2, fontWeight: "bold" }}>
            {practiceName}
          </Text>
        </Text>
      ) : (
        <Text style={typographyStyle.body2}>
          {`This analysis is created for `}
          <Text style={{ ...typographyStyle.body2, fontWeight: "bold" }}>
            {supplierName}
          </Text>
          {` internal use only.`}
        </Text>
      )}

      <Text style={typographyStyle.body2}>
        {`Created by: `}
        <Text style={{ ...typographyStyle.body2, fontWeight: "bold" }}>
          {ownerName}
        </Text>
        {` of `}
        <Text style={{ ...typographyStyle.body2, fontWeight: "bold" }}>
          {supplierName}
        </Text>
      </Text>
      <Text style={typographyStyle.body2}>
        Page {pageIndex + 1} of {totalPage}
      </Text>
      <PDFSerialNumber serial={serial ?? ""} />
    </View>
  );
};

const ReviewPageFooter = ({
  pageIndex = 0,
  totalPage,
  analysis,
}: PDFPageFooterProps) => {
  const { pageStyle, typographyStyle } = useAnalysisPDFStyles();

  const { adviserName, firmName, serial, familyGroupName } =
    analysis?.summary ?? new AnalysisSummaryDTO();

  return (
    <View style={pageStyle.footer}>
      <Text style={typographyStyle.body2}>
        {`This Suitability Review is for: `}
        <Text style={{ ...typographyStyle.body2, fontWeight: "bold" }}>
          {familyGroupName}
        </Text>

        {`. Data valid as at: `}
        <Text style={{ ...typographyStyle.body2, fontWeight: "bold" }}>
          {`${format(
            new Date(analysis?.databaseVersion ?? new Date()),
            DateFormat,
          )}`}
        </Text>
      </Text>

      <Text style={typographyStyle.body2}>
        {`Prepared by: `}
        <Text style={{ ...typographyStyle.body2, fontWeight: "bold" }}>
          {adviserName}
        </Text>
        {` of `}
        <Text style={{ ...typographyStyle.body2, fontWeight: "bold" }}>
          {firmName}
        </Text>
      </Text>
      <Text style={typographyStyle.body2}>
        Page {pageIndex + 1} of {totalPage}
      </Text>
      <PDFSerialNumber serial={serial ?? ""} />
    </View>
  );
};
